import React, { PropTypes } from "react";
import {
  ControlLabel,
  FormControl,
  FormGroup,
  HelpBlock,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";

class TextInput extends React.Component {
  componentWillMount() {
    const { input, fixed, type } = this.props;

    if (
      input &&
      input.value &&
      fixed &&
      (type === "text" || type === "absNumber")
    ) {
      input.value = parseFloat(input.value).toFixed(fixed);
    }
  }

  render() {
    const {
      input,
      label,
      meta: { touched, error, warning },
      groupClassName,
      id,
      isStatic,
      overrideState,
      onChange,
      onBlur,
      inputAddon,
      type = "text",
      formatOverlay,
      fieldDescription,
      fieldDescriptionAfterError,
      fieldTooltip,
      link,
      className,
      onFocus,
      readOnly,
      required,
      handleOnInput,
      displayLogo,
      getExtraFormattedError,
      formattedMessageErrorValues,
      customOnBlurFunc,
      handleOnPaste,
      ...rest
    } = this.props;

    let validationState = touched && !!error ? error.state || "error" : null;
    let validationMessageKey =
      !!error && error.messageKey ? error.messageKey : "error.unknown";
    let validationValues = !!error && error.values ? error.values : {};

    if (touched && !!warning) {
      /**
       * Allows us to display error on field, but also make the request to the api. With the error option, no api call is made.
       */
      validationState = warning.state;
      validationMessageKey = warning.messageKey;
      validationValues = warning.values ? warning.values : {};
    }

    const scene = this.context.scene ? "-" + this.context.scene : "";

    const handleKeyPress = (e) => {
      if (type === "number" || type === "absNumber") {
        const allowed = [13, 46, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57];
        if (type === "number") {
          allowed.push(45);
        }
        if (!e.ctrlKey && !allowed.includes(e.which)) {
          e.preventDefault();
        }
      }

      if(this.props.handleKeyPressCB) {
        this.props.handleKeyPressCB(e);
      }
    };

    const renderDisplayLogo = () => {
      if(displayLogo) {
        if((touched && error) || (touched && warning)) {
          const allValidationValues = {...validationValues, ...formattedMessageErrorValues};
          return (
            <div className="d-flex">
            <HelpBlock>
              <FormattedMessage
                id={validationMessageKey}
                values={allValidationValues}
              />
              {getExtraFormattedError && (
                getExtraFormattedError()
              )}
            </HelpBlock>
              <span className="text-input-logo ml-auto">{displayLogo()}</span>
            </div>
          );
        }

        return (
          <span className="text-input-logo">{displayLogo()}</span>
        );
      }
    };

    const onBlurFunc = (e) => {
      input.onBlur(e);
      return onBlur && onBlur(e);
    };

    const generateControl = () => {
      const inputType =
        // existing numbers fields are styled with a text input
        type === "number"
          ? "text"
          : // but for a number steppers we need an actual number input
          type === "stepper"
          ? "number"
          : type;
      const formControlClassName = classNames({
        "number-stepper": inputType === "number",
        [className]: className,
        link: link,
      });

      let formControl = (
        <FormControl
          {...input}
          {...rest}
          readOnly={readOnly}
          type={inputType}
          onKeyPress={handleKeyPress.bind(this)}
          onPaste={handleOnPaste ? handleOnPaste: undefined}
          onInput={handleOnInput ? handleOnInput : undefined} /** Can detect CTRL + V , CTRL + X, and DEL buttons. */
          onChange={(e) => {
            e.persist();
            onChange && onChange(e);
            return input.onChange(e);
          }}
          onBlur={customOnBlurFunc ? customOnBlurFunc : onBlurFunc}
          onFocus={(e) => {
            if (readOnly) return;
            onFocus && onFocus(e);
            return input.onFocus(e);
          }}
          className={formControlClassName}
        />
      );
      if (link) {
        formControl = (
          <a href={link} target="_blank">
            {formControl}
          </a>
        );
      }
      return fieldTooltip ? (
        <OverlayTrigger
          overlay={
            <Tooltip id={id} placement="bottom">
              {fieldTooltip}
            </Tooltip>
          }
          placement="bottom"
        >
          {formControl}
        </OverlayTrigger>
      ) : (
        formControl
      );
    };

    const inputClasses = classNames({
      input: true,
      overlayed: !!formatOverlay,
    });

    return (
      <FormGroup
        className={groupClassName}
        controlId={id + scene}
        validationState={overrideState || validationState}
      >
        {label && (
          <ControlLabel>
            {label}
            {required ? <span className="required"> *</span> : ""}
          </ControlLabel>
        )}
        <div className="input-wrapper">
          <div className={inputClasses} data-overlay={formatOverlay}>
            {isStatic ? (
              <FormControl.Static>{input.value}</FormControl.Static>
            ) : (
              generateControl()
            )}
          </div>
          {inputAddon && <InputGroup.Addon>{inputAddon}</InputGroup.Addon>}

          {!fieldDescriptionAfterError && fieldDescription && (
            <small className="form-text text-muted">{fieldDescription}</small>
          )}
          {displayLogo && (
            renderDisplayLogo()
          )}
          {!displayLogo && ((touched && error) || (touched && warning)) && (
            <HelpBlock>
              <FormattedMessage
                id={validationMessageKey}
                values={validationValues}
              />
            </HelpBlock>
          )}
          

          {fieldDescriptionAfterError && fieldDescription && (
            <small className="form-text text-muted">{fieldDescription}</small>
          )}
        </div>
      </FormGroup>
    );
  }
}

TextInput.propTypes = {
  fieldDescription: PropTypes.object,
  fieldDescriptionAfterError: PropTypes.bool,
  fieldTooltip: PropTypes.object,
  groupClassName: PropTypes.string,
  id: PropTypes.string.isRequired,
  input: PropTypes.object.isRequired,
  inputAddon: PropTypes.string,
  isStatic: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  meta: PropTypes.object.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  overrideState: PropTypes.string,
  type: PropTypes.string.isRequired,
  validation: PropTypes.object,
  validationKey: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  link: PropTypes.string,
  className: PropTypes.string,
  getExtraFormattedError: PropTypes.func,
  onFocus: PropTypes.func,
  handleKeyPressCB: PropTypes.func,
  handleOnInput: PropTypes.func,
  displayLogo: PropTypes.func,
  handleOnPaste: PropTypes.func,
  fixed: PropTypes.bool,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  formattedMessageErrorValues: PropTypes.object,
  customOnBlurFunc: PropTypes.func,
  checkTextAreaLength: PropTypes.func,
  formatOverlay: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.bool,
  ]),
};

TextInput.defaultProps = {
  fieldDescriptionAfterError: false,
  type: "text",
  formatOverlay: false,
};

TextInput.contextTypes = {
  scene: PropTypes.string,
};

export default TextInput;



// WEBPACK FOOTER //
// ./src/components/common/form/TextInput.js